import { memo } from "react";

// Highchart
import Highcharts from "highcharts/highcharts-gantt";
import HighchartsReact from "highcharts-react-official";
import exporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";

import "assets/styles/components/highcharts.css";

// MUI Components
import { Box, Typography } from "@mui/material";
import theme from "assets/styles/themes";

// Components
import CustomIcon from "components/CustomIcon";
import { SortMenu } from "components/SortMenu";
import { DateRange } from "components/DateRange";
import { CustomiseLabels } from "../components/CustomiseLabels";
import { TransactionHistoryPopup } from "./components/TransactionHistoryPopup";
import { ExportMenu } from "./components/ExportMenu";

// Icons
import icons from "enums/icons";

// Hooks
import useTenure from "./hooks/useTenure";

// Utils
import { isEmpty } from "lodash";

// Types
import { tenureChartOrderBy } from "services/tenureChart";
import { IBaseChartProps } from "components/Charts/chart.types";
import React from "react";

// Highcharts export module
exporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

const sortMenuOptions = [
    {
        id: tenureChartOrderBy[0],
        sortBy: "longest-shortest" as const,
        label: "Total Tenure",
    },
    {
        id: tenureChartOrderBy[1],
        sortBy: "longest-shortest" as const,
        label: "Current Tenure",
    },
];

const Tenure = memo(function Tenure({
    getData,
    hideRefSeries,
    moreOptions,
}: Omit<IBaseChartProps, "width" | "height">): JSX.Element {
    const {
        sort,
        chartRef,
        dateRange,
        chartOptions,
        sharedHistory,
        hasSharedHistories,
        openDateFilter,
        tenureChartData,
        transactionPopover,
        roleTenure,
        showTransactions,
        customiseLabels,
        disabledOptions,
        handleSetCustomiseLabels,
        exportWithLegend,
        handleSort,
        setDateRange,
        handleToggleSharedHistory,
        handleToggleDateFilter,
        handleToggleTransactionsPopover,
        handleToggleTransactionLines,
        handleToggleCurrentPrior,
    } = useTenure({
        getData,
        hideRefSeries,
    });

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (type?: "image/png" | "image/svg+xml") => {
        if (type) {
            exportWithLegend(chartRef.current?.chart, type);
        }
        setAnchorEl(null);
    };

    return (
        <Box position="relative" sx={{ overflow: "hidden", width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    background: theme.palette.background.grey,
                    height: 48,
                    padding: "0 16px",
                    mb: 2,
                    borderRadius: 1,
                }}
            >
                <Typography sx={{ fontSize: 12, mr: 1.5 }}>Sort By:</Typography>
                {sortMenuOptions.map((menu) => (
                    <Box key={menu.id} sx={{ display: "flex", alignItems: "center", mr: 1.5 }}>
                        <SortMenu
                            columnId={menu.id}
                            currentSortBy={sort}
                            onSort={handleSort}
                            sortConfig={{ sortBy: menu.sortBy }}
                            label={
                                <Typography sx={{ fontSize: 12, ml: 1, color: theme.palette.text.primary }}>
                                    {menu.label}
                                </Typography>
                            }
                            icon={
                                <CustomIcon
                                    icon={icons.sortArrows}
                                    sx={{ fontSize: 14, color: theme.palette.neutrals.dark }}
                                />
                            }
                        />
                    </Box>
                ))}
                <Box sx={{ marginLeft: "auto" }}>
                    <DateRange
                        open={openDateFilter}
                        dates={dateRange}
                        startDateLimit={2001}
                        handleToggle={handleToggleDateFilter}
                        setDates={setDateRange}
                    />
                </Box>
            </Box>

            <ExportMenu
                roleTenure={roleTenure}
                sharedHistory={sharedHistory}
                showTransactions={showTransactions}
                hasSharedHistories={hasSharedHistories}
                anchorEl={anchorEl}
                open={open}
                handleToggleCurrentPrior={handleToggleCurrentPrior}
                handleToggleTransactionLines={handleToggleTransactionLines}
                handleToggleSharedHistory={handleToggleSharedHistory}
                handleClick={handleClick}
                handleClose={handleClose}
            />

            {!isEmpty(tenureChartData.transactions) && (
                <TransactionHistoryPopup
                    data={tenureChartData.transactions}
                    popoverConfig={transactionPopover}
                    handleToggle={handleToggleTransactionsPopover}
                />
            )}

            <Box sx={{ mb: 2 }}>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
            </Box>

            {!moreOptions?.hideCustomizeLabels && (
                <CustomiseLabels
                    options={customiseLabels}
                    disabledOptions={disabledOptions}
                    setOptions={handleSetCustomiseLabels}
                />
            )}
        </Box>
    );
});

export default Tenure;
